import styled from "styled-components";
import Image from "next/image";
import { textStyles, toCSS } from "design-system/foundation/typography";
import { colors, spacing, radius, stroke } from "design-system/foundation";
import { breakpoints } from "styles";

export const PracticeLeaderboardWrapper = styled.section`
  &,
  & *,
  & *::before,
  & *::after {
    box-sizing: border-box;
  }
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${spacing["5xl"]} ${spacing["2xl"]};
  gap: ${spacing["3xl"]};

  @media ${breakpoints.tablet} {
    padding: ${spacing["5xl"]} ${spacing["8xl"]};
    overflow-y: auto;
  }
`;

export const LeaderboardTitle = styled.p`
  text-align: start;
  margin: ${spacing.none};
  ${toCSS(textStyles.heading.h5)}
  color: ${colors.text.base.primary};
`;

export const LeaderboardFilter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

export const FilterTab = styled.button<{ $isActive: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${spacing["2xl"]} ${spacing.none};
  text-align: center;
  border-bottom: ${({ $isActive }) =>
    $isActive ? `${colors.border.state.focus} solid ${stroke.md}` : "none"};
  width: 100%;
  ${toCSS(textStyles.caption.caption6)}
  color: ${({ $isActive }) =>
    $isActive
      ? `${colors.text.semantic.brand}`
      : `${colors.text.base.primary}`};
  transition: all 0.3s ease-out;
`;

export const LoadingContainer = styled.div`
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

export const LeaderboardInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing["2xl"]};
`;

export const InfoContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const InfoTitle = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.body.body4Regular)}
  color: ${colors.text.base.secondary};
  text-align: center;
`;

export const InfoDuration = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing["2xs"]};
  justify-content: center;
`;

export const DurationTimer = styled.p`
  margin: ${spacing.none};
  border-radius: ${radius.xs};
  padding: ${spacing["2xs"]};
  background: ${colors.bg.warning.primarySoft};
  ${toCSS(textStyles.body.body5Bold)};
  color: ${colors.text.semantic.brand};
  text-align: center;
`;

export const TimerSeparator = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.body.body5Bold)};
  color: ${colors.text.semantic.brand};
  text-align: center;
`;

export const Divider = styled.div`
  background: ${colors.border.state.default};
  width: ${stroke.xs};
`;

export const InfoGradeContainer = styled(InfoContainer)`
  @media ${breakpoints.mobile} {
    flex: none;
    width: 120px;
  }
`;

export const InfoText = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.body.body3Bold)}
  color: ${colors.text.base.primary};
  text-align: center;
`;

export const PositionRank = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing["2xs"]};
  border-radius: ${radius.sm};
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  text-align: center;
`;

export const RankLabel = styled.p`
  border-radius: ${radius.xs} ${radius.xs} 0 0;
  margin: ${spacing.none};
  ${toCSS(textStyles.body.body6Bold)}
`;

export const RankNumber = styled.h2`
  margin: ${spacing.none};
  ${toCSS(textStyles.heading.h5)}
  color: ${colors.text.base.invert};
`;

export const PositionDesc = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.caption.caption6)}
  display: flex;
  align-items: center;
`;

export const LeaderboardPosition = styled.div<{ $isActive: boolean }>`
  display: flex;
  flex-direction: row;
  gap: ${spacing.lg};
  border: ${stroke.md} solid
    ${({ $isActive }) =>
      $isActive
        ? `${colors.border.semantic.success}`
        : `${colors.border.state.disable}`};
  border-radius: ${radius.xl};
  background: ${({ $isActive }) =>
    $isActive
      ? `${colors.bg.success.secondarySoft}`
      : `${colors.bg.base.disabled}`};
  padding: ${spacing["2xl"]};

  ${PositionRank} {
    background: ${({ $isActive }) =>
      $isActive
        ? `${colors.bg.success.primaryBold}`
        : `${colors.border.state.disable}`};
    border: ${stroke.md} solid
      ${({ $isActive }) =>
        $isActive
          ? `${colors.bg.success.primaryBold}`
          : `${colors.border.state.disable}`};
  }

  ${RankLabel} {
    background: ${({ $isActive }) =>
      $isActive
        ? ` ${colors.bg.success.secondarySoft}`
        : `${colors.bg.base.disabled}`};
    color: ${({ $isActive }) =>
      $isActive
        ? `${colors.text.semantic.success}`
        : ` ${colors.text.base.disabled}`};
  }

  ${PositionDesc} {
    color: ${({ $isActive }) =>
      $isActive
        ? `${colors.text.semantic.success}`
        : ` ${colors.text.base.disabled}`};
  }
`;
export const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing["3xl"]};
`;

export const ListStudent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  padding: ${spacing["3xl"]};
  border-radius: ${radius["2xl"]};
  border: ${stroke.md} solid ${colors.border.state.default};
`;

export const StudentRank = styled.div`
  width: 40px;
  border-radius: ${radius.sm};
  padding: ${spacing.sm};
  background: ${colors.bg.base.disabled};
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const Rank = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.caption.caption5)};
  color: ${colors.text.semantic.neutral};
`;

export const StudentNameContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
`;

export const StudentName = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.caption.caption6)};
  color: ${colors.text.base.primary};
`;

export const StudentBadge = styled.span`
  margin: ${spacing.none};
  padding: ${spacing["2xs"]} ${spacing.md};
  border-radius: ${radius["3xl"]};
  ${toCSS(textStyles.body.body6Bold)};
  color: ${colors.text.base.invert};
  white-space: nowrap;
`;

export const StudentPoint = styled.p`
  ${toCSS(textStyles.body.body5Regular)};
  color: ${colors.text.base.secondary};
`;

export const Student = styled.div<{ $isActive: boolean; $isTop10: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing["2xl"]};

  ${({ $isActive }) =>
    $isActive &&
    `
    margin: ${spacing.none} -${spacing["3xl"]};
    padding: ${spacing.lg} ${spacing["3xl"]};
    border-radius: ${radius.xl};
    ${Rank} {
      color: ${colors.text.base.invert};
    }
  `}

  ${({ $isActive, $isTop10 }) =>
    $isActive &&
    $isTop10 &&
    `
    background: ${colors.bg.success.secondarySoft};
    border: ${stroke.md} solid ${colors.border.semantic.success};
    ${StudentRank} {
      background: ${colors.bg.success.primaryBold};
    }
    ${StudentName} {
      color: ${colors.text.semantic.success};
    }
    ${StudentBadge} {
      background: ${colors.bg.success.primaryBold};
    }
    ${StudentPoint} {
      ${toCSS(textStyles.body.body5Bold)};
      color: ${colors.text.semantic.success};
    }
  `}

  ${({ $isActive, $isTop10 }) =>
    $isActive &&
    !$isTop10 &&
    `
    background: ${colors.bg.base.disabled};
    border: ${stroke.md} solid ${colors.border.state.disable};
    ${StudentRank} {
      background: ${colors.border.state.disable};
    }
    ${StudentName} {
      color: ${colors.text.semantic.neutral};
    }
    ${StudentBadge} {
      background: ${colors.text.base.secondary};
    }
    ${StudentPoint} {
      ${toCSS(textStyles.body.body5Bold)};
      color: ${colors.text.semantic.neutral};
    }
  `}
`;

export const LeaderboardDivider = styled.div`
  width: 100%;
  height: ${stroke.xs};
  background: ${colors.border.state.default};
`;

export const LeaderboardEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing["2xl"]};
  padding: ${spacing["5xl"]};
`;

export const EmptyImage = styled(Image)`
  padding: ${spacing.md};
  width: 160px;
  height: 160px;
`;

export const EmptyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  text-align: center;
`;

export const TextTitle = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.heading.h5)}
  color: ${colors.text.base.primary};
`;

export const TextDesc = styled.p`
  margin: ${spacing.none};
  ${toCSS(textStyles.body.body4Regular)}
  color: ${colors.text.base.secondary};
`;
